import React, { useState, useEffect, useRef } from "react";
import { X, ExternalLink, Save, Loader2, Upload, ImageIcon } from "lucide-react";
import { machinesApi, ejerciciosApi, formatApiError } from "../lib/api";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const GRUPOS = [
  "Femoral", "Quadriceps", "Glúteo", "Gemelo", "aductor", "abductor", "Lumbar",
  "espalda", "pecho", "Hombro", "Biceps", "Triceps", "brazos",
  "core", "cardio", "Full body", "posterior_cadena", "accesorio",
];
const ESFUERZOS = ["Empuje", "Tracción", "Pierna", "Cardio", "Core", "Isométrico"];
const DIFICULTADES = [{ val: null, label: "No especificada" }, { val: 1, label: "1 · Fácil" }, { val: 2, label: "2 · Medio" }, { val: 3, label: "3 · Avanzado" }];

const _parseGrupos = (raw) => {
  if (!raw) return { primary: "", secondary: [] };
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  return { primary: parts[0] || "", secondary: parts.slice(1) };
};

// machine = existing doc for edit, or { numero:"", nombre:"", activa:true, ... } for new
// zones = array of {codigo, nombre} for the planta dropdown
export default function MachineEditModal({ machine, zones = [], onClose, onSaved }) {
  const isNew = !machine._existing;
  const [form, setForm] = useState({ ...machine });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Multi-select grupo muscular
  const _pg = _parseGrupos(machine.grupo_muscular);
  const [primaryGrupo, setPrimaryGrupo] = useState(_pg.primary);
  const [secondaryGrupos, setSecondaryGrupos] = useState(_pg.secondary);

  const addSecondary = (val) => {
    if (!val || secondaryGrupos.includes(val) || val === primaryGrupo) return;
    setSecondaryGrupos((prev) => [...prev, val]);
  };
  const removeSecondary = (val) => setSecondaryGrupos((prev) => prev.filter((g) => g !== val));
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileInputRef = useRef(null);

  // Exercises state — read-only display for this machine
  const [exercises, setExercises] = useState([]);
  const [exLoading, setExLoading] = useState(false);

  useEffect(() => {
    setForm({ ...machine });
    setError("");
    setUploadErr("");
    const pg = _parseGrupos(machine.grupo_muscular);
    setPrimaryGrupo(pg.primary);
    setSecondaryGrupos(pg.secondary);
  }, [machine]);

  useEffect(() => {
    if (!isNew && machine.numero) {
      setExLoading(true);
      ejerciciosApi.adminList(machine.numero)
        .then(setExercises)
        .catch(() => {})
        .finally(() => setExLoading(false));
    }
  }, [isNew, machine.numero]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) { setUploadErr("Guarda la máquina primero para poder subir fotos."); return; }
    setUploading(true); setUploadErr("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/admin/maquinas/${machine.numero}/upload-foto`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("foto_url", res.data.foto_url);
    } catch (e) {
      setUploadErr(formatApiError(e?.response?.data?.detail) || "Error subiendo la imagen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fotoSrc = form.foto_url
    ? form.foto_url.startsWith("/api/")
      ? `${BACKEND}${form.foto_url}`
      : form.foto_url
    : null;

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const allGrupos = [primaryGrupo, ...secondaryGrupos].filter(Boolean);
      const payload = {
        nombre: form.nombre,
        planta: form.planta || null,
        grupo_muscular: allGrupos.length ? allGrupos.join(", ") : null,
        tipo_esfuerzo: form.tipo_esfuerzo || null,
        dificultad: form.dificultad !== undefined ? form.dificultad : null,
        foto_url: form.foto_url || null,
        video_url: form.video_url || null,
        instrucciones_breves: form.instrucciones_breves || null,
        activa: form.activa,
      };
      if (isNew) {
        // numero is auto-generated by the backend — don't send it
        const created = await machinesApi.create(payload);
        onSaved(created);
      } else {
        await machinesApi.update(machine.numero, payload);
        onSaved({ ...form });
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  // Build planta options: zones from DB + any existing value not in list
  const plantaOptions = zones.map((z) => ({ value: z.codigo, label: `${z.codigo} — ${z.nombre}` }));
  if (form.planta && !zones.find((z) => z.codigo === form.planta)) {
    plantaOptions.unshift({ value: form.planta, label: form.planta });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="machine-edit-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#0d0d0d] border border-white/10 shadow-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0d0d0d] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            {!isNew && <span className="bg-[#FFCC00] text-black font-black text-xs px-2 py-0.5 tracking-widest mr-3">{machine.numero}</span>}
            <span className="text-sm font-bold text-white/80">{isNew ? "Nueva máquina" : "Editar máquina"}</span>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors" data-testid="modal-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">

          {/* Nombre */}
          <Field label="Nombre *">
            <input
              value={form.nombre || ""}
              onChange={(e) => set("nombre", e.target.value)}
              data-testid="edit-nombre"
              className="input-dark"
              placeholder="Leg Press"
            />
          </Field>

          {/* Planta + Esfuerzo */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Zona / Planta">
              <select
                value={form.planta || ""}
                onChange={(e) => set("planta", e.target.value || null)}
                data-testid="edit-planta"
                className="input-dark appearance-none cursor-pointer"
              >
                <option value="">— Sin zona —</option>
                {plantaOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Tipo esfuerzo">
              <Select value={form.tipo_esfuerzo || ""} onChange={(v) => set("tipo_esfuerzo", v || null)} options={["", ...ESFUERZOS]} testId="edit-esfuerzo" />
            </Field>
          </div>

          {/* Grupo muscular multi-select */}
          <Field label="Grupo muscular principal">
            <select
              value={primaryGrupo}
              onChange={(e) => {
                const v = e.target.value;
                setPrimaryGrupo(v);
                setSecondaryGrupos((prev) => prev.filter((g) => g !== v));
              }}
              data-testid="edit-grupo"
              className="input-dark appearance-none cursor-pointer"
            >
              <option value="">— Sin especificar —</option>
              {GRUPOS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>

          {/* Grupos secundarios */}
          <div>
            <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-white/35 mb-2">Músculos secundarios</label>
            <select
              defaultValue=""
              onChange={(e) => { addSecondary(e.target.value); e.target.value = ""; }}
              data-testid="edit-grupo-secondary"
              className="input-dark appearance-none cursor-pointer text-white/50"
            >
              <option value="">+ Añadir músculo secundario</option>
              {GRUPOS.filter((g) => g !== primaryGrupo && !secondaryGrupos.includes(g)).map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            {secondaryGrupos.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="grupo-chips">
                {secondaryGrupos.map((g) => (
                  <span key={g} className="inline-flex items-center gap-1 bg-white/8 border border-white/12 text-white/70 text-[10px] uppercase tracking-wider px-2 py-1">
                    {g}
                    <button type="button" onClick={() => removeSecondary(g)} className="text-white/40 hover:text-white/90 ml-0.5">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Dificultad + Activa */}
          <div className="grid grid-cols-2 gap-4 items-end">
            <Field label="Dificultad">
              <Select
                value={form.dificultad !== null && form.dificultad !== undefined ? String(form.dificultad) : ""}
                onChange={(v) => set("dificultad", v === "" ? null : Number(v))}
                options={DIFICULTADES.map((d) => ({ value: d.val !== null ? String(d.val) : "", label: d.label }))}
                testId="edit-dificultad"
              />
            </Field>
            <Field label="Estado">
              <button
                type="button"
                onClick={() => set("activa", !form.activa)}
                data-testid="edit-activa-toggle"
                className={`w-full py-2.5 text-[11px] font-black tracking-[0.2em] uppercase border transition-colors ${
                  form.activa
                    ? "bg-green-500/10 border-green-500/40 text-green-400 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400"
                    : "bg-red-500/10 border-red-500/30 text-red-400 hover:bg-green-500/10 hover:border-green-500/40 hover:text-green-400"
                }`}
              >
                {form.activa ? "Activa" : "Inactiva"}
              </button>
            </Field>
          </div>

          {/* Foto */}
          <Field label="Foto de la máquina">
            <div className="space-y-3">
              {fotoSrc && (
                <div className="relative w-full h-36 bg-[#0a0a0a] border border-white/10 overflow-hidden">
                  <img src={fotoSrc} alt="Preview" className="w-full h-full object-cover" data-testid="foto-preview" />
                </div>
              )}
              {!fotoSrc && (
                <div className="w-full h-24 bg-[#0a0a0a] border border-dashed border-white/15 flex items-center justify-center text-white/20">
                  <ImageIcon size={24} />
                </div>
              )}
              {!isNew && (
                <div className="flex gap-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border font-bold text-[11px] tracking-[0.2em] uppercase cursor-pointer transition-colors
                    ${uploading ? "border-white/10 text-white/30" : "border-[#FFCC00]/50 text-[#FFCC00] hover:bg-[#FFCC00]/10"}`}
                    data-testid="upload-foto-btn">
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {uploading ? "Subiendo..." : "Subir foto"}
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      disabled={uploading} onChange={handlePhotoUpload} data-testid="foto-file-input" />
                  </label>
                  {fotoSrc && (
                    <a href={fotoSrc} target="_blank" rel="noreferrer" className="flex items-center px-3 border border-white/10 text-white/40 hover:text-[#FFCC00] hover:border-[#FFCC00]/40 transition-colors">
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              )}
              {isNew && <p className="text-[10px] text-white/25">Podrás subir foto una vez creada la máquina.</p>}
              {uploadErr && <p className="text-red-400 text-xs" data-testid="upload-error">{uploadErr}</p>}
              <div>
                <p className="text-[9px] tracking-[0.18em] uppercase text-white/25 mb-1">O pega una URL externa</p>
                <input value={form.foto_url || ""} onChange={(e) => set("foto_url", e.target.value)}
                  data-testid="edit-foto-url" className="input-dark" placeholder="https://..." />
              </div>
            </div>
          </Field>

          {/* Video URL */}
          <Field label="URL Video (YouTube embed o externo)">
            <div className="flex gap-2">
              <input value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)}
                data-testid="edit-video-url" className="input-dark flex-1" placeholder="https://youtube.com/embed/..." />
              {form.video_url && (
                <a href={form.video_url} target="_blank" rel="noreferrer" className="flex items-center px-3 border border-white/10 text-white/40 hover:text-[#FFCC00] hover:border-[#FFCC00]/40 transition-colors">
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </Field>

          {/* Instrucciones */}
          <Field label="Instrucciones breves">
            <textarea value={form.instrucciones_breves || ""} onChange={(e) => set("instrucciones_breves", e.target.value)}
              data-testid="edit-instrucciones" rows={4} className="input-dark resize-none"
              placeholder="Ajusta el asiento hasta que... Mantén la espalda apoyada..." />
          </Field>

          {error && (
            <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3" data-testid="edit-error">{error}</p>
          )}

          {/* ── Exercises — read-only for this machine ── */}
          {!isNew && (
            <div className="border-t border-white/8 pt-5">
              <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">
                Ejercicios asignados {!exLoading && `(${exercises.length})`}
              </label>
              {exLoading ? (
                <div className="flex items-center gap-2 text-white/30 text-xs py-2">
                  <Loader2 size={12} className="animate-spin" /> Cargando…
                </div>
              ) : exercises.length === 0 ? (
                <p className="text-white/20 text-xs py-2">Sin ejercicios asignados. Gestiona desde la pestaña <span className="text-[#FFCC00]/60 font-bold">Ejercicios</span>.</p>
              ) : (
                <div className="space-y-1.5" data-testid="exercises-readonly-list">
                  {exercises.map((ex) => (
                    <div key={ex.id} className="flex items-center justify-between px-3 py-2 bg-[#111] border border-white/8" data-testid={`ex-row-${ex.codigo}`}>
                      <div>
                        <span className="text-[9px] font-black tracking-widest text-[#FFCC00]/60 mr-2">{ex.codigo}</span>
                        <span className="text-xs font-bold text-white">{ex.nombre}</span>
                      </div>
                      <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-1 border ${
                        ex.activa ? "border-green-500/20 text-green-400/70" : "border-red-500/20 text-red-400/50"
                      }`}>
                        {ex.activa ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={handleSave} disabled={saving || !form.nombre?.trim()} data-testid="edit-save-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase py-3.5 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Guardando..." : isNew ? "Crear máquina" : "Guardar cambios"}
            </button>
            <button onClick={onClose} data-testid="edit-cancel-btn"
              className="px-6 border border-white/15 text-white/60 text-[11px] font-bold tracking-[0.2em] uppercase hover:border-white/40 hover:text-white transition-colors">
              Cancelar
            </button>
          </div>
        </div>
      </div>

      <style>{`.input-dark { width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.12); color: #fff; padding: 10px 14px; font-size: 13px; outline: none; transition: border-color .15s; } .input-dark:focus { border-color: #FFCC00; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="input-dark appearance-none cursor-pointer">
      {options.map((opt) =>
        typeof opt === "string"
          ? <option key={opt} value={opt}>{opt || "— Sin especificar"}</option>
          : <option key={opt.value} value={opt.value}>{opt.label}</option>
      )}
    </select>
  );
}
